// apps/web/src/features/admin/form-config/layouts/api.ts
import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api-client";

/**
 * LayoutVariantDto — mirrors the admin DTO built server-side in
 * apps/api/src/routes/job-types/forms/layout-variants.ts (Task 10). A
 * near-identical shape also lives in `features/classify/api/client.ts` for
 * the station's read-only lookup — that copy stays independent on purpose
 * (different feature, different lifecycle) rather than importing across
 * feature boundaries.
 */
export interface LayoutVariantDto {
  id: string;
  label: string;
  isBaseline: boolean;
  status: "active" | "retired";
  observationCount: number;
  divergentObservationCount: number;
  lastMatchedAt: string | null;
  createdAt: string;
  isStale: boolean;
}

export interface CanDeclare {
  allowed: boolean;
  reason: string | null;
}

/**
 * useLayoutVariants — wraps the Task 10 admin REST surface for
 * FormLayoutVariant. Pattern mirrors `../forms/useJobTypeForms.ts`: same
 * useState/useEffect/useCallback shape, same `api.*` wrapper, self-refreshing
 * mutations.
 *
 * Returns:
 *   variants    — ordered as the API returns them (createdAt asc); [] while loading
 *   canDeclare  — whether the form may gain another declared layout
 *   loading     — true during the initial fetch
 *   rename      — PATCH { label }
 *   setStatus   — PATCH { status: 'active' | 'retired' } (409 cannot_retire_baseline
 *                 on a baseline row — the UI never offers the action, this is
 *                 the server's own belt-and-suspenders check)
 *   merge       — POST /:variantId/merge { targetVariantId }; 204 on success
 *   remove      — DELETE /:variantId; 409 cannot_delete_baseline
 *   refetch     — re-fetch the list
 */
export function useLayoutVariants(jobTypeId: string, formId: string | null) {
  const [variants, setVariants] = useState<LayoutVariantDto[]>([]);
  const [canDeclare, setCanDeclare] = useState<CanDeclare>({ allowed: false, reason: null });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!jobTypeId || !formId) {
      setVariants([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await api.get<{ variants: LayoutVariantDto[]; canDeclare: CanDeclare }>(
        `/job-types/${jobTypeId}/forms/${formId}/layout-variants`,
      );
      setVariants(Array.isArray(data.variants) ? data.variants : []);
      setCanDeclare(data.canDeclare ?? { allowed: false, reason: null });
    } catch {
      setVariants([]);
    } finally {
      setLoading(false);
    }
  }, [jobTypeId, formId]);

  useEffect(() => {
    void load();
  }, [load]);

  const rename = useCallback(
    async (variantId: string, label: string): Promise<void> => {
      await api.patch(`/job-types/${jobTypeId}/forms/${formId}/layout-variants/${variantId}`, { label });
      await load();
    },
    [jobTypeId, formId, load],
  );

  const setStatus = useCallback(
    async (variantId: string, status: "active" | "retired"): Promise<void> => {
      await api.patch(`/job-types/${jobTypeId}/forms/${formId}/layout-variants/${variantId}`, { status });
      await load();
    },
    [jobTypeId, formId, load],
  );

  const merge = useCallback(
    async (variantId: string, targetVariantId: string): Promise<void> => {
      await api.post(
        `/job-types/${jobTypeId}/forms/${formId}/layout-variants/${variantId}/merge`,
        { targetVariantId },
      );
      await load();
    },
    [jobTypeId, formId, load],
  );

  const remove = useCallback(
    async (variantId: string): Promise<void> => {
      await api.delete(`/job-types/${jobTypeId}/forms/${formId}/layout-variants/${variantId}`);
      await load();
    },
    [jobTypeId, formId, load],
  );

  const refetch = useCallback(async () => {
    await load();
  }, [load]);

  return { variants, canDeclare, loading, rename, setStatus, merge, remove, refetch };
}
